// src/lib/buildSnapshotFindings.js
//
// Ticket 023-snapshotredesign — pure content-authoring module for the
// Snapshot result page (TriageResults.js).
//
// Input:  facts (result.derived_facts), allFlags (result.flags), taxYear
// Output: { findings, notableChips, prose, timelineNodes }
//
// This is NOT the same as generate-assessment's LLM-driven narration — every
// sentence here is deterministic, built from the same booleans/flags the
// existing chapter-based TriageResults.js already reads (see "What to reuse
// from the existing build" in the plan). Nothing here re-derives tax logic;
// it only selects and phrases what runTriageFlags.ts/deriveFacts.ts already
// computed.
//
// COPY STATUS: every sentence below is a first draft, written from the
// ticket's four worked examples + its nine accuracy-fix rules + the
// already-correct ROW_CAVEATS/flag-summary text as source material (per D7).
// It is explicitly NOT final — John reviews/finalizes the actual wording in
// a dedicated content/voice pass on the PR before merge (D7), separate from
// QA (which checks the *branches* this module takes, not the prose).

import { getSlotLabel } from './slotConfig'
import { buildIrpfRows, BASE_RATE_TEXT, BECKHAM_RATE_TEXT, ROW_CAVEATS, beckhamSelfEmploymentCaveat } from '../components/TaxBaseExplainer'
import { modelo720Flag as findModelo720Flag, wealthFlag as findWealthFlag } from './triageViewFacts'

const BASE_ORDER = ['general', 'savings', 'uncertain']

const SPELLED_OUT = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']
function spellOut(n) {
  return SPELLED_OUT[n] || String(n)
}

// ─── Notable chips / prose (D2) ─────────────────────────────────────────────

const NOTABLE_GROUP_TEMPLATES = {
  classification_review: count =>
    `${spellOut(count).replace(/^\w/, c => c.toUpperCase())} income type${count === 1 ? '' : 's'} need${count === 1 ? 's' : ''} individual review`,
}

// spainFlags per D2's own convention — matches deriveComplexityTier's
// existing "category !== 'us'" scoping, i.e. category === 'spain'
// (the only two categories a flag can otherwise carry are 'us' and
// 'general', and 'general' is never used by any flag pushed today).
function computeNotable(allFlags) {
  const spainFlags = (allFlags || []).filter(f => f.category === 'spain')
  const notableFlags = spainFlags.filter(f => f.notable)

  const standaloneChips = notableFlags.filter(f => !f.notableGroup).map(f => f.notableChipText)

  const groupCounts = {}
  notableFlags.filter(f => f.notableGroup).forEach(f => {
    groupCounts[f.notableGroup] = (groupCounts[f.notableGroup] || 0) + 1
  })
  const groupChips = Object.entries(groupCounts).map(([groupId, count]) => {
    const template = NOTABLE_GROUP_TEMPLATES[groupId]
    return template ? template(count) : `${spellOut(count)} items need review`
  })

  const chips = [...standaloneChips, ...groupChips]
  const totalUnits = standaloneChips.length + Object.keys(groupCounts).length

  return { chips, totalUnits }
}

function buildNotableSection(allFlags) {
  const { chips, totalUnits } = computeNotable(allFlags)

  if (totalUnits >= 2) {
    return { notableChips: chips, prose: null }
  }
  if (totalUnits === 1) {
    // DRAFT — placeholder pending D7's content-review checkpoint (the exact
    // final wording for the "1 notable unit" branch is explicitly deferred,
    // not a blocker). QA verifies this renders as prose, not a lone chip —
    // not this exact sentence.
    return { notableChips: null, prose: 'One thing here is worth a closer look before you file — see below.' }
  }
  return {
    notableChips: null,
    prose: 'Nothing additional stands out from the answers you gave. Your situation appears relatively straightforward from this Snapshot.',
  }
}

// ─── Finding 1: residency + prior-filing exposure ──────────────────────────

function moveYearPhrase(facts, taxYear) {
  if (!facts.moveYear) return null
  if (facts.moveYear === taxYear) return `Your ${monthYear(facts)} move makes ${taxYear} your first full calendar year of Spanish residency`
  if (facts.moveYear < taxYear) {
    const yrs = taxYear - facts.moveYear
    const ordinal = yrs === 1 ? 'first' : yrs === 2 ? 'second' : yrs === 3 ? 'third' : `${yrs}th`
    return `Your ${monthYear(facts)} move would make ${taxYear} your ${ordinal} full year as a Spanish resident, though that depends on your actual residency facts`
  }
  return null
}

function monthYear(facts) {
  const monthName = facts.moveMonth
    ? new Date(0, facts.moveMonth - 1).toLocaleString('en', { month: 'long' })
    : null
  return monthName ? `${monthName} ${facts.moveYear}` : String(facts.moveYear)
}

function buildResidencyFinding(facts, taxYear) {
  if (facts.isNonResident) {
    // Accuracy fix #6 — non-resident framing is about residency, not value.
    const sentences = [
      `You're a confirmed non-resident for ${taxYear}. You won't file an annual IRPF return this year.`,
      "As a non-resident, Spain only reaches Spain-sourced income and Spain-situated assets, not your worldwide income or wealth. Where something below doesn't apply to you, it's usually because of your non-resident status, not the amounts involved.",
    ]
    return { id: 'residency', title: "You're not a Spanish tax resident this year.", body: sentences }
  }

  if (!facts.isSpanishResident) {
    return {
      id: 'residency',
      title: 'Your Spanish tax residency needs confirmation.',
      body: ['Your Spanish tax residency status for this year is not fully confirmed. That determines whether you file IRPF on worldwide income or only on Spanish-sourced income.'],
    }
  }

  const priorGap = facts.filedSpainBefore === false && !facts.moveIsThisYear
  const moveSentence = moveYearPhrase(facts, taxYear)

  const sentences = []
  if (moveSentence) {
    sentences.push(`${moveSentence}.`)
  } else {
    sentences.push(`You're a confirmed Spanish tax resident for ${taxYear}.`)
  }

  let title
  if (priorGap) {
    title = "You're likely a resident, and prior years may need a look."
    sentences.push("You told us you've never filed a Spanish return. If earlier years also qualified, those years may still be open — worth raising with an advisor first.")
  } else if (facts.filedSpainBefore === true) {
    title = "You're a Spanish tax resident, filing as usual."
    sentences.push("You've filed in Spain before — no prior-year gaps to flag.")
  } else {
    title = "You're a Spanish tax resident."
  }

  if (facts.beckhamActive) {
    // 025-beckhamincome QA fix: this used to say employment income was
    // "Spanish-sourced income only," which directly contradicted the new
    // BECKHAM_RATE_TEXT.general caption on the income table on the same
    // page ("worldwide for employment income") - D5's own research is that
    // employment specifically is taxed worldwide under Beckham's flat rate;
    // it's other income types (investments, rental, etc.) that are
    // genuinely limited to Spanish-source amounts.
    sentences.push("Under the Beckham regime (Modelo 151), your employment income is taxed worldwide at a flat 24% (47% above €600,000) — other income types are generally limited to Spanish-source amounts, unlike the full worldwide taxation that applies under ordinary IRPF residency.")
  }

  return { id: 'residency', title, body: sentences }
}

// ─── Finding 2: self-employment / autónomo ─────────────────────────────────

function buildAutonomoFinding(facts) {
  const sentences = [
    // D11 register: "apply to most", not "are due" (universal) or "may
    // apply" (too hedgy) — matches the runTriageFlags.ts fix. Inline link to
    // the autónomo calculator lives in this sentence (content-review pass,
    // 023, item 5) — plain inline text, not a CTA; ResultCTA/sidebar still
    // owns the primary CTA.
    {
      pre:      'Spanish Social Security (RETA) registration, and quarterly IRPF (Modelo 130) and IVA (Modelo 303) filings that apply to most autónomos, sit alongside your annual return; the ',
      href:     '/autonomo-calculator',
      linkText: 'autónomo calculator',
      post:     ' can help you estimate both.',
    },
  ]
  if (facts.isUsPerson) {
    // Accuracy fix #5 — state the general rule directly, don't over-hedge.
    sentences.push('Because you live and work in Spain, the US–Spain totalization agreement generally points your coverage to the Spanish system.')
    // D2 (ticket 034): the actual payoff of the totalization-agreement
    // passage above — agreed in principle during 023/025's content pass,
    // never actually built. Exact wording is a content-review call like
    // every other sentence in this file (see COPY STATUS header).
    sentences.push('A Spanish certificate of coverage documents this, exempting you from US self-employment tax on this income.')
  }
  return { id: 'autonomo', title: 'Self-employment comes with more filing obligations.', body: sentences }
}

// ─── Finding 3: income classification table ────────────────────────────────

// Same baseline-row construction TriageResults.js uses (do-not-re-derive per
// the plan) — income types that never get their own irpf_component flag.
//
// D12 (ticket 023): flaggedRentalOrPension is narrowed to just the two flags
// that actually represent rental income — the old broader guard (which also
// included the two pension-treaty flag ids) silently suppressed this row for
// anyone whose pension flag fired, even with real, unrelated rental income.
// Fixed identically here and in TriageResults.js so both pages agree.
function buildBaselineRows(facts, irpfComponentFlags) {
  const flaggedRentalOrPension = irpfComponentFlags.some(f =>
    ['spanish_rental', 'imputed_rent'].includes(f.id)
  )
  const rows = []
  if (facts.hasSpanishEmployment) rows.push({ id: 'baseline_employment_es', label: 'Spanish employment salary', base: 'general', confidence: 'high' })
  if (facts.hasUsEmployment) rows.push({ id: 'baseline_employment_us', label: 'US employment income', base: 'general', confidence: 'high' })
  if (facts.hasSelfEmployment) {
    // D4 (ticket 025): mirrors TriageResults.js's identical branching -
    // self-employment/Beckham compatibility hinges on an ENISA
    // "entrepreneurial activity" qualification, not sourcing, so this
    // becomes uncertain/low under Beckham rather than an asserted
    // classification either way.
    rows.push(facts.beckhamActive
      ? { id: 'baseline_self_employment', label: 'Self-employment income', base: 'uncertain', confidence: 'low', caveat: beckhamSelfEmploymentCaveat(facts.selfEmploymentCountry) }
      : { id: 'baseline_self_employment', label: 'Self-employment income', base: 'general', confidence: 'high' }
    )
  }
  if (facts.hasInvestments) {
    // D3 (ticket 025): mirrors TriageResults.js's identical branching -
    // triage collects no source-country data for investment income, and
    // foreign-source investment income is genuinely exempt under Beckham
    // while Spanish-source isn't, so this becomes uncertain/low rather than
    // a binary taxable/exempt call under Beckham.
    rows.push(facts.beckhamActive
      ? { id: 'baseline_investments', label: 'Dividends and interest', base: 'uncertain', confidence: 'low', caveat: ROW_CAVEATS.baseline_investments }
      : { id: 'baseline_investments', label: 'Dividends and interest', base: 'savings', confidence: 'high' }
    )
  }
  if (facts.pensionIsSpanish) {
    // D6 (ticket 025): no row-construction change under Beckham - Spain-
    // sourced by definition, so the sourcing ambiguity that applies to
    // investments/rental doesn't apply here. Caption-only fix via
    // BECKHAM_RATE_TEXT.general (D5) instead.
    rows.push({ id: 'baseline_pension_spanish', label: 'Spanish pension income', base: 'general', confidence: 'high' })
  }
  if (facts.hasRentalIncome && !facts.hasSpanishRental && !flaggedRentalOrPension) {
    // Confidence corrected low → high (content-review pass, 023): the paid
    // calculator's own incomeClassificationRules.ts already classifies
    // foreign_property rental as classification_confidence: 'high' with no
    // review_flags ("Classification is settled... High confidence.") — the
    // triage/Snapshot 'low' tag here was drift, not a real uncertainty.
    //
    // D3 (ticket 025): under Beckham, this row is already gated on confirmed
    // foreign sourcing, and research states foreign real-estate income is
    // neither declared nor taxed in Spain under Beckham while the regime
    // applies - a more confident, exemption-leaning claim than the
    // investments case above, mirrors TriageResults.js's identical branching.
    rows.push(facts.beckhamActive
      ? { id: 'baseline_foreign_rental', label: 'Foreign rental income', base: 'uncertain', confidence: 'low', caveat: ROW_CAVEATS.baseline_foreign_rental }
      : { id: 'baseline_foreign_rental', label: 'Foreign rental income', base: 'general', confidence: 'high' }
    )
  }
  return rows
}

// D4 — the compact table's OWN review-tag threshold: confidence !== 'high'
// (covers 'low' AND 'medium'), a different/local mechanism from the old
// chapter page's 'low'-only tag and from D2's notable/notableGroup design.
function needsReview(row) {
  return !!row.confidence && row.confidence !== 'high'
}

function buildIncomeTableFinding(facts, irpfComponentFlags, allFlags, taxYear) {
  const baselineRows = buildBaselineRows(facts, irpfComponentFlags)
  const rows = buildIrpfRows(irpfComponentFlags, baselineRows, facts.businessPartnershipType)
  if (rows.length === 0) return null

  // Single-year-event marking (property/investment/crypto sale) inside the
  // existing capital_gains row, per the dense worked example's pattern —
  // tested as a standalone finding and failed the notability test. Only
  // capital_gains carries a spanishTaxBase mapping; inheritance/gift has no
  // such mapping (D8), so it can never be marked this way.
  const soldSomething = facts.soldProperty || facts.soldInvestments || facts.soldCrypto
  const taggedRows = rows.map(row => {
    if (row.id !== 'capital_gains' || !soldSomething) return row
    return {
      ...row,
      label: facts.soldProperty && !facts.soldInvestments && !facts.soldCrypto ? 'Property sale gain' : row.label,
      eventNote: `${taxYear} event`,
    }
  })

  const presentBases = BASE_ORDER.filter(base => taggedRows.some(r => r.base === base))
  const boxCount = presentBases.length
  const reviewRows = taggedRows.filter(needsReview)

  let title
  if (boxCount <= 1) {
    title = 'Your income is straightforward — one type, one base.'
  } else if (reviewRows.length > 0) {
    const count = spellOut(reviewRows.length).replace(/^\w/, c => c.toUpperCase())
    title = `Your income splits across ${boxCount === 2 ? 'two tax bases' : 'multiple tax bases'}. ${count} item${reviewRows.length === 1 ? '' : 's'} need${reviewRows.length === 1 ? 's' : ''} individual review.`
  } else {
    title = boxCount === 2 ? 'Your income splits cleanly across two bases.' : 'Your income splits across multiple bases.'
  }

  const sentences = []

  // D3 (ticket 034): establishes "everything below funnels into one return"
  // before the box-count-specific detail sentences. D1 (ticket 036): moved
  // out of body/sentences into its own top-level leadSentence field so
  // SnapshotFinding.js can render it before the table instead of after it —
  // text itself is unchanged, this is a pure move.
  const leadSentence = 'This income all counts toward one return: your IRPF (Modelo 100).'

  // D4/D5 (ticket 036): the old `if (boxCount <= 1) {...} else if
  // (reviewRows.length > 0) {...} else {...}` had a real bug — a profile
  // whose only present income base happened to be low/medium confidence
  // (boxCount === 1) fell into the boxCount branch and never got the "why
  // review flags" explanation at all, even though the table's own "Review"
  // tag still rendered on that row (needsReview() doesn't check boxCount).
  // The boxCount <= 1 sentence itself is cut entirely (D4) — it only
  // restated the title ("one type, one base") and the table's sole column,
  // both already visible on the page. Collapsing to a plain
  // reviewRows.length gate (D5) fixes that visibility gap as a named
  // side-effect, not a silent one.
  if (reviewRows.length > 0) {
    // Ticket 025: falls back to row.caveat when no matching flag summary
    // exists — baseline rows (baseline_investments/baseline_foreign_rental/
    // baseline_self_employment) aren't flags, so they never match anything
    // in allFlags. Without this fallback, a Beckham filer's baseline review
    // rows would enter reviewRows (confidence: 'low', per D3/D4) but their
    // caveat text would never actually reach this sentence — the only place
    // this compact page surfaces row-level caveats at all (unlike the
    // chapter page's IncomeTaxTable, which renders row.caveat directly per
    // row). Existing flag-based rows are unaffected: their summary lookup
    // already succeeds, so this fallback never triggers for them.
    const caveats = reviewRows
      .map(row => (allFlags || []).find(f => f.id === row.id)?.summary || row.caveat)
      .filter(Boolean)
    // D2 (ticket 036): bulleted instead of one joined paragraph
    // (caveats.join(' ')) — caveats computation itself is unchanged, only
    // how it's packaged for rendering.
    sentences.push({ type: 'bulletList', lead: 'Why the review flags?', items: caveats })
  } else {
    sentences.push('No review flags this year. These are settled classifications.')
  }

  // Content-review fix (023): foreign rental is a settled, high-confidence
  // classification (see buildBaselineRows), but it's still taxed net of
  // expenses/depreciation, not on gross rent — matches the calculator's own
  // "trust net income and depreciation entered by the user" treatment.
  //
  // Ticket 025 (D3): gated on !beckhamActive — under Beckham this row is no
  // longer the settled, high-confidence classification the sentence assumes
  // (it's now uncertain/low, and the review caveat above states rental
  // income from outside Spain is generally not taxed in Spain at all under
  // Beckham). Showing this "taxed net of expenses" line unconditionally
  // would directly contradict that caveat.
  if (taggedRows.some(row => row.id === 'baseline_foreign_rental') && !facts.beckhamActive) {
    sentences.push('Foreign rental income is taxed net of allowable expenses and depreciation, not on the gross rent received.')
  }

  // D10 (ticket 034): capital_gains/equity_compensation rows otherwise get no
  // explanation at all when confidence is high (never enters reviewRows above)
  // — the one real information-loss gap found in this ticket's audit, since
  // RSU vesting / asset sales are common scenarios. Reads the row's own flag
  // summary directly, same allFlags.find lookup the reviewRows caveat
  // sentence uses above, just not gated on needsReview/confidence.
  //
  // D3 (ticket 036): partial, named reversal of the above — capital_gains is
  // dropped from this block. Its summary only restates what the table
  // already shows (the row's eventNote tag plus the savings-base column's
  // own rate-range caption, BASE_RATE_TEXT.savings, identical text), so
  // cutting it loses zero information. equity_compensation is kept: its
  // apportionment-for-split-year-grants fact is real and not shown anywhere
  // else on the page.
  taggedRows
    .filter(row => row.id === 'equity_compensation')
    .forEach(row => {
      const summary = (allFlags || []).find(f => f.id === row.id)?.summary
      if (summary) sentences.push(summary)
    })

  // D7 (ticket 032): unconditional in-line link to the calculator, every time
  // this Finding renders (i.e. for every resident — the !facts.isNonResident
  // gate at the call site already governs whether this Finding exists at
  // all). linkText/href deliberately match the marriage Finding's link
  // verbatim — same destination, same label.
  sentences.push({
    pre:      'For exact numbers on how each base is taxed, the ',
    href:     '/calculator',
    linkText: 'calculator',
    post:     ' can run this with your real income.',
  })

  const rateText = {}
  BASE_ORDER.forEach(base => {
    rateText[base] = (facts.beckhamActive && BECKHAM_RATE_TEXT[base]) || BASE_RATE_TEXT[base]
  })

  return {
    id: 'income_table',
    title,
    leadSentence,
    body: sentences,
    table: { rows: taggedRows, presentBases, rateText },
  }
}

// ─── Finding 4: wealth (Modelo 720 / ITSGF / Patrimonio) ───────────────────

function assetSplitFindingSentence(assetSplit) {
  if (assetSplit === 'roughly_equal') {
    // D7 (ticket 036): reworded per John's "shorten it, use clearer
    // language" direction — was "Because you told us assets are split
    // roughly evenly between you, asset reporting is the more certain
    // trigger here — each of you will need to report your own assets
    // carefully, and individual wealth-tax exposure isn't automatic just
    // because the split is even." (023, item 4 reword).
    return "Since your assets are split roughly evenly, you'll each need to report your own share carefully — an even split doesn't automatically trigger individual wealth tax."
  }
  if (assetSplit === 'one_spouse_more' || assetSplit === 'all_one_spouse') {
    // D7 (ticket 036): tightened for consistency with the reworded sibling
    // branch above — was "Because you told us assets are concentrated with
    // one of you, that spouse's individual position may look different
    // from your household total."
    return "Since assets are concentrated with one of you, that spouse's individual reporting position may look different from your household total."
  }
  return null
}

function buildWealthFinding(facts, allFlags) {
  const spainFlags = (allFlags || []).filter(f => f.category === 'spain')
  const modelo720 = findModelo720Flag(spainFlags)
  const wealth = findWealthFlag(spainFlags)

  const showModelo720 = !!modelo720 || facts.hasReportableForeignAssets
  const showWealth = !!wealth || facts.hasReportableNetWorth
  if (!showModelo720 && !showWealth) return null

  const sentences = []
  if (showModelo720) {
    // Content-review fix (023, item 3): state the actual €50,000-per-category
    // threshold, pulled from runTriageFlags.ts's modelo_720 flag summary
    // ("...foreign assets above €50,000 per category must file..."), instead
    // of the vaguer "may apply based on what you reported."
    sentences.push(modelo720
      ? 'Modelo 720 is a foreign-asset reporting regime, not a tax. It applies once a single category of foreign assets (accounts, securities, or real estate) crosses €50,000, and based on what you reported, that threshold applies to you.'
      : "Modelo 720 is a foreign-asset reporting regime, not a tax. It applies once a single category of foreign assets (accounts, securities, or real estate) crosses €50,000; based on what you reported, it likely doesn't apply to you this year.")
  }
  if (showWealth) {
    if (wealth?.id === 'solidarity_tax') {
      // Accuracy fix #2 — state the confident version. D5 (ticket 033): names
      // Modelo 718 per John's own direction, replacing the "too AI'ish"
      // version with a more direct one.
      sentences.push("Despite regional wealth-tax exemptions like Madrid and Andalucía's, you may still owe national wealth tax (Modelo 718 for ITSGF) once your net worth crosses the national threshold — those exemptions don't apply above that line.")
    } else if (wealth?.id === 'wealth_tax') {
      sentences.push(`Separately, your net worth may trigger regional wealth tax (Patrimonio, Modelo 714) in ${getSlotLabel('comunidad_autonoma', facts.comunidad) || 'your region'}. Rates and exemptions vary significantly by region.`)
    } else {
      sentences.push("Your reported net worth is below the level where wealth tax or ITSGF would apply this year — worth tracking if it grows.")
    }
  }
  // D6 (ticket 036): asset-split call removed from this Finding — it was
  // also called from buildMarriageFinding, so the same paragraph could
  // appear twice on one page. Kept only in buildMarriageFinding, which
  // already carries its own lead-in sentence ("Wealth tax and Modelo 720
  // are also assessed per person, not per household") immediately before it.

  return { id: 'wealth', title: 'Your foreign assets and net worth trigger two separate wealth-related obligations.', body: sentences }
}

// ─── Finding 5: marriage ────────────────────────────────────────────────────

function buildMarriageFinding(facts, allFlags) {
  if (!facts.isMarried) return null
  const spainFlags = (allFlags || []).filter(f => f.category === 'spain')
  const assetsRelevant = !!findModelo720Flag(spainFlags) || !!findWealthFlag(spainFlags) || facts.hasReportableForeignAssets || facts.hasReportableNetWorth

  const sentences = []
  if (facts.isUsPerson) {
    sentences.push('Spain treats spouses as separate IRPF taxpayers by default, though joint filing is available in qualifying circumstances.')
    // Inline link to the calculator's joint/individual comparison
    // (content-review pass, 023, item 5) — plain inline text, not a CTA;
    // ResultCTA/sidebar still owns the primary CTA.
    sentences.push({
      pre:      'Whether it helps depends on how income splits between you; the ',
      href:     '/calculator',
      linkText: 'calculator',
      post:     ' can compare both scenarios with your actual numbers.',
    })
  } else {
    sentences.push('Spain treats spouses as separate IRPF taxpayers by default; joint filing is available but usually only helps when one spouse has little income.')
  }

  if (assetsRelevant) {
    sentences.push('Wealth tax and Modelo 720 are also assessed per person, not per household.')
    const gotcha = assetSplitFindingSentence(facts.assetSplit)
    if (gotcha) sentences.push(gotcha)
  }

  return {
    id: 'marriage',
    // Content-review reword (023, item 4): not every user is US-based, and
    // "MFJ" is US-specific jargon — state the actual point directly instead.
    title: facts.isUsPerson ? "Being married doesn't automatically mean filing jointly." : 'Filing individually is the default for married couples.',
    body: sentences,
  }
}

// ─── Standalone finding: inheritance / gift (D8 — always its own finding) ──

function buildInheritanceFinding(facts, allFlags) {
  if (!facts.hasInheritanceOrGift) return null
  const flag = (allFlags || []).find(f => f.id === 'inheritance_spain')
  const body = flag?.summary
    ? [flag.summary]
    : ["Spain's inheritance and gift tax (ISD) is set regionally, so the rate and any exemption can vary a lot depending on where you live and your relationship to the giver."]
  // D8 (ticket 036): linkOut removed — it pointed at
  // /guides/spain-inheritance-gift-tax, a route that never existed
  // anywhere in src/App.js (confirmed via grep), so the link 404'd.
  // SnapshotFinding.js's generic finding.linkOut render branch is left in
  // place (not inheritance-specific scaffolding); see "Also noticed."
  return {
    id: 'inheritance',
    title: 'Your inheritance or gift may trigger Spanish tax.',
    body,
  }
}

// ─── Timeline (compressed) ──────────────────────────────────────────────────

const TIMELINE_TITLES = {
  autonomo_setup:              'Registration before starting self-employment',
  autonomo_obligations:        'Modelo 130/303 — Quarterly filings',
  modelo_720:                  'Modelo 720 — Foreign assets reporting',
  irpf:                        'Modelo 100 — Annual return',
  beckham_active:              'Modelo 151 — Annual return',
  solidarity_tax:              'Modelo 718 — ITSGF',
  wealth_tax:                  'Modelo 714 — Patrimonio (wealth tax)',
  first_time_filer:            'First-time filing setup',
  inheritance_spain:           'Modelo 650/651 — Inheritance or gift (ISD)',
  spanish_rental_nonresident:  'Modelo 210 — Non-resident rental income',
  imputed_rent_nonresident:    'Modelo 210 — Non-resident imputed rent',
  capital_gains_nonresident:   'Modelo 210 — Non-resident property sale',
}

// D1 (ticket 033): a flag produces a timeline node only if its id is in this
// allowlist — every allowed, present flag gets its own node, never merged
// with another regardless of whether they share a date. Replaces 032's
// TIMELINE_GROUP_PRIORITY grouping model entirely.
const TIMELINE_ALLOWED_IDS = [
  'autonomo_setup', 'autonomo_obligations', 'modelo_720', 'irpf', 'beckham_active',
  'solidarity_tax', 'wealth_tax', 'first_time_filer', 'inheritance_spain',
  'spanish_rental_nonresident', 'imputed_rent_nonresident', 'capital_gains_nonresident',
]

function bucketLabel(sortDate, taxYear) {
  const [year, month] = sortDate.split('-').map(Number)
  if (month <= 3) return `Jan – Mar ${year}`
  if (month <= 6) return `Apr – Jun ${year}`
  if (month <= 9) return `Jul – Sep ${year}`
  return `Oct – Dec ${year}`
}

// D1 (ticket 033): returns structured stepper nodes, not a joined string.
// No grouping — a flag produces a node only if its id is in
// TIMELINE_ALLOWED_IDS and it has a non-null deadline. Every allowed,
// present flag gets its own node, never merged with another even if they
// share a date.
export function buildTimeline(allFlags, taxYear) {
  const relevant = (allFlags || []).filter(f => TIMELINE_ALLOWED_IDS.includes(f.id) && f.deadline)
  const ongoing = relevant.filter(f => !(f.deadline.type === 'fixed' && f.deadline.sortDate))
  const dated = relevant
    .filter(f => f.deadline.type === 'fixed' && f.deadline.sortDate)
    .sort((a, b) => a.deadline.sortDate.localeCompare(b.deadline.sortDate))

  const nodes = []
  ongoing.forEach(f => {
    nodes.push({
      id: f.id,
      title: TIMELINE_TITLES[f.id] || f.title,
      dateLabel: f.deadline.type === 'quarterly' ? 'Quarterly, year-round' : f.deadline.label,
    })
  })
  dated.forEach(f => {
    nodes.push({
      id: f.id,
      title: TIMELINE_TITLES[f.id] || f.title,
      dateLabel: bucketLabel(f.deadline.sortDate, taxYear),
    })
  })
  return nodes
}

// ─── Main entry point ───────────────────────────────────────────────────────

export default function buildSnapshotFindings(facts, allFlags, taxYear) {
  const spainFlags = (allFlags || []).filter(f => f.category === 'spain')
  const irpfComponentFlags = spainFlags.filter(f => f.taskCategory === 'irpf_component')

  const { notableChips, prose } = buildNotableSection(allFlags)
  const timelineNodes = buildTimeline(allFlags, taxYear)

  const findings = []
  // Residency finding always renders — even the "needs confirmation" branch
  // is a real, personalized conclusion (per the four editorial rules).
  findings.push(buildResidencyFinding(facts, taxYear))
  if (facts.hasSelfEmployment) findings.push(buildAutonomoFinding(facts))
  // Non-residents don't get the general/savings IRPF table — same gate
  // TriageResults.js uses (showEducationalSections && !isNonResident).
  if (!facts.isNonResident) {
    const incomeFinding = buildIncomeTableFinding(facts, irpfComponentFlags, allFlags, taxYear)
    if (incomeFinding) findings.push(incomeFinding)
  }
  const wealthFinding = buildWealthFinding(facts, allFlags)
  if (wealthFinding) findings.push(wealthFinding)
  const marriageFinding = buildMarriageFinding(facts, allFlags)
  if (marriageFinding) findings.push(marriageFinding)
  const inheritanceFinding = buildInheritanceFinding(facts, allFlags)
  if (inheritanceFinding) findings.push(inheritanceFinding)

  return { findings, notableChips, prose, timelineNodes }
}
